/**
 * "For You" lightweight personalization — MVP scoring.
 * Uses: favorites, recent searches, recent categories, user city.
 */

import { filterLiveListings } from './liveListing.js';

/* ------------------------------------------------------------------
   Recent Categories — per-user localStorage, v2 scoping.

   Same anti-leak pattern as Recently Viewed / Hidden Listings:
     • Versioned key `wassla_recent_categories_v2_<userId>`.
     • ALL operations require a real, non-empty userId. Empty strings,
       the sentinel `"guest"`, null, undefined → no-op writes / [] reads.
     • No unscoped fallback. Legacy keys are swept on login / logout
       from AuthContext.
   ------------------------------------------------------------------ */

const PREFIX_V2 = 'wassla_recent_categories_v2_';
const LEGACY_PREFIX_V1 = 'wassla_recent_categories_';
const LEGACY_SHARED_KEY = 'wassla_recent_categories';

function isValidUserId(userId) {
  if (typeof userId !== 'string') return false;
  const trimmed = userId.trim();
  if (!trimmed) return false;
  if (trimmed === 'guest') return false;
  return true;
}

function getCatKey(userId) {
  return isValidUserId(userId) ? `${PREFIX_V2}${userId}` : null;
}

export function sweepLegacyRecentCategoryKeys() {
  try {
    localStorage.removeItem(LEGACY_SHARED_KEY);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(LEGACY_PREFIX_V1) && !k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}

export function purgeAllRecentCategories() {
  try {
    sweepLegacyRecentCategoryKeys();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}

// Save category interaction
export function saveRecentCategory(category, userId) {
  if (!category || category === 'all' || category === 'recommended') return;
  if (!isValidUserId(userId)) return;
  const key = getCatKey(userId);
  if (!key) return;
  try {
    const current = JSON.parse(localStorage.getItem(key) || '[]');
    const safeCurrent = Array.isArray(current)
      ? current.filter((c) => typeof c === 'string' && c)
      : [];
    const next = [category, ...safeCurrent.filter((c) => c !== category)].slice(0, 15);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

// Get recent categories
export function getRecentCategories(userId) {
  if (!isValidUserId(userId)) return [];
  const key = getCatKey(userId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((c) => typeof c === 'string' && c)
      : [];
  } catch {
    return [];
  }
}

// Extract preferred categories from interactions
export function extractPreferredCategories(recentCategories = [], favoriteAds = []) {
  const counts = {};
  recentCategories.forEach(c => { counts[c] = (counts[c] || 0) + 2; });
  favoriteAds.forEach(ad => {
    const cat = ad?.category_id || ad?.category || '';
    if (cat) counts[cat] = (counts[cat] || 0) + 3;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([cat]) => cat)
    .slice(0, 5);
}

// Score a single listing for personalization
export function scoreListingForYou(listing, signals) {
  let score = 0;

  const category = listing?.category_id || listing?.category || '';
  const title = (listing?.title || listing?.title_en || '').toLowerCase();
  const city = (listing?.city || (typeof listing?.location === 'string' ? listing?.location : '') || '').toLowerCase();

  const preferredCategories = signals?.preferredCategories || [];
  const recentSearches = (signals?.recentSearches || []).map(s => String(s).toLowerCase());
  const userCity = String(signals?.city || '').toLowerCase();

  // Category affinity (strongest signal)
  if (preferredCategories.includes(category)) score += 5;

  // Search keyword match
  for (const kw of recentSearches) {
    if (kw && title.includes(kw)) { score += 3; break; }
  }

  // City proximity
  if (userCity && city && city.includes(userCity)) score += 4;

  // Recency boost (newer = higher)
  const created = new Date(listing?.created_at || 0).getTime();
  const daysSince = (Date.now() - created) / 86400000;
  if (daysSince < 1) score += 3;
  else if (daysSince < 3) score += 2;
  else if (daysSince < 7) score += 1;

  // Boosted items get a small bump
  if (listing?.is_boosted) score += 1;

  return score;
}

// Check if we have enough signals for personalization
export function hasEnoughSignals(signals) {
  return (
    (signals?.preferredCategories?.length || 0) > 0 ||
    (signals?.recentSearches?.length || 0) > 0 ||
    Boolean(signals?.city)
  );
}

// Generate the For You feed
export function generateForYouFeed(allListings, signals) {
  // iter315 — Use the canonical live-listing predicate. Real ads ship
  // as `status: "active"`; the old `status === 'published'` filter
  // rejected everything and contributed to the Home "No ads are live
  // yet" bug.
  const active = filterLiveListings(allListings);

  if (!hasEnoughSignals(signals)) {
    // Cold start: newest first
    return [...active].sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
  }

  return [...active]
    .map(item => ({ ...item, _fyScore: scoreListingForYou(item, signals) }))
    .sort((a, b) => {
      if (b._fyScore !== a._fyScore) return b._fyScore - a._fyScore;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
}
